import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import '../../styles/AdmissionsDashboard.css';

const BRANCH_STORAGE_KEY = 'admissionsDashboard.branch';

const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  const then = new Date(dateString).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'Never';
  return date.toLocaleString();
};

const locationLine = (admission) => {
  const branch = admission.branch || '';
  const ward = admission.ward || '';
  const head = [branch, ward].filter(Boolean).join(' · ');
  if (!admission.bed) return head;
  const bed = String(admission.bed);
  const bedText = /bed/i.test(bed) ? bed : `Bed ${bed}`;
  return `${head} / ${bedText}`;
};

const patientName = (admission) => {
  const patient = admission.Patient || {};
  return `${patient.patient_first_name || ''} ${patient.patient_last_name || ''}`.trim() || 'Patient';
};

const statusLabel = (view) => {
  const status = view?.status;
  if (!status?.label) return 'Admitted';
  const ago = formatRelativeTime(status.at);
  return ago ? `${status.label} · ${ago}` : status.label;
};

const watchFindingText = (item) => String(item?.label || '').replace(' · ', ' ');

const vitalsDetailText = (vitals) => {
  if (!vitals) return 'No vitals yet';
  const risk = vitals.risk || 'Unknown';
  const score = vitals.score ?? 0;
  const ago = formatRelativeTime(vitals.taken_at) || formatDateTime(vitals.taken_at);
  const stale = vitals.stale ? ' (overdue)' : '';
  const watch = Array.isArray(vitals.watch) ? vitals.watch : [];
  if (watch.length) {
    const names = watch.map(watchFindingText).join('; ');
    return `${risk} risk · Score (${score}). ${names} — doctor should review · ${ago}${stale}`;
  }
  return `${risk} risk · Score (${score}) · ${ago}${stale}`;
};

const isCriticalVitals = (vitals) => {
  if (!vitals) return false;
  const risk = String(vitals.risk || '').toLowerCase();
  if (risk === 'high' || risk === 'medium') return true;
  return Array.isArray(vitals.watch) && vitals.watch.length > 0;
};

const isCriticalGlucose = (glucose) => {
  const band = String(glucose?.band || '').toLowerCase();
  return band === 'low' || band === 'high';
};

const readStoredBranch = () => {
  try {
    return sessionStorage.getItem(BRANCH_STORAGE_KEY) || '';
  } catch (error) {
    return '';
  }
};

const AdmissionsDashboard = () => {
  const [admissions, setAdmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(readStoredBranch);
  const [expandedId, setExpandedId] = useState(null);
  const admissionsRef = useRef([]);
  admissionsRef.current = admissions;

  const fetchAdmissions = useCallback(async () => {
    const hasRows = admissionsRef.current.length > 0;
    try {
      if (hasRows) setRefreshing(true);
      else setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admissions/dashboard`
      );
      setAdmissions(Array.isArray(response.data) ? response.data : []);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      console.error('Error fetching admissions dashboard:', err);
      setError('Failed to load admissions data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchAdmissions();
    const interval = setInterval(fetchAdmissions, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchAdmissions]);

  const branches = useMemo(() => {
    const values = [...new Set(admissions.map((admission) => admission.branch).filter(Boolean))];
    return values.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [admissions]);

  useEffect(() => {
    if (!selectedBranch) return;
    if (branches.length && !branches.includes(selectedBranch)) {
      setSelectedBranch('');
    }
  }, [branches, selectedBranch]);

  const chooseBranch = (branch) => {
    setSelectedBranch(branch);
    try {
      sessionStorage.setItem(BRANCH_STORAGE_KEY, branch);
    } catch (error) {
      // Ignore storage failures on locked-down tablets.
    }
  };

  const visibleAdmissions = useMemo(() => {
    if (!selectedBranch) return admissions;
    return admissions.filter((admission) => admission.branch === selectedBranch);
  }, [admissions, selectedBranch]);

  const missingConsentCount = visibleAdmissions.filter(
    (admission) => !admission.dashboard_data?.consent_complete
  ).length;
  const readyForDischargeCount = visibleAdmissions.filter(
    (admission) => admission.clinical_status === 'ready_for_discharge'
  ).length;

  const toggleExpanded = (admissionId) => {
    setExpandedId((current) => (current === admissionId ? null : admissionId));
  };

  if (loading && admissions.length === 0) {
    return (
      <div className="admissions-dashboard">
        <div className="dashboard-header">
          <h1>Open Admissions Dashboard</h1>
        </div>
        <div className="loading">Loading admissions...</div>
      </div>
    );
  }

  return (
    <div className="admissions-dashboard">
      <div className="dashboard-header">
        <h1>Open Admissions Dashboard</h1>
        <div className="header-actions">
          <span className="last-updated">
            Last updated: {lastUpdated ? formatDateTime(lastUpdated) : 'Never'}
          </span>
          <button onClick={fetchAdmissions} className="refresh-btn" disabled={loading || refreshing}>
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error">
          {error}
          <button type="button" className="refresh-btn" onClick={fetchAdmissions}>
            Try again
          </button>
        </div>
      )}

      {admissions.length > 0 && (
        <div className="branch-bar">
          <span className="branch-label">Which branch?</span>
          <div className="branch-buttons" role="group" aria-label="Which branch?">
            <button
              type="button"
              className={!selectedBranch ? 'active' : ''}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => chooseBranch('')}
            >
              All
            </button>
            {branches.map((branch) => (
              <button
                key={branch}
                type="button"
                className={selectedBranch === branch ? 'active' : ''}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => chooseBranch(branch)}
              >
                {branch}
              </button>
            ))}
          </div>
        </div>
      )}

      {admissions.length === 0 ? (
        !error && (
          <div className="no-admissions">
            <p>No open admissions found.</p>
          </div>
        )
      ) : (
        <>
          {readyForDischargeCount > 0 && (
            <div className="ready-discharge-alert-banner">
              <span className="consent-alert-icon">→</span>
              <span>
                <strong>{readyForDischargeCount}</strong> patient{readyForDischargeCount > 1 ? 's' : ''} ready for discharge.
                Pharmacy can fill take-home medicines, then admin can enter the discharge date.
              </span>
            </div>
          )}
          {missingConsentCount > 0 && (
            <div className="consent-alert-banner">
              <span className="consent-alert-icon">⚠</span>
              <span>
                <strong>{missingConsentCount}</strong> admission{missingConsentCount > 1 ? 's' : ''} missing consent form{missingConsentCount > 1 ? 's' : ''}.
                Please collect signed consent as soon as possible.
              </span>
            </div>
          )}

          {visibleAdmissions.length === 0 ? (
            <div className="no-admissions">
              <p>No open admissions for this branch.</p>
            </div>
          ) : (
            <div className="admissions-list">
              <div className="admissions-list-head">
                <span>Patient</span>
                <span>Status</span>
                <span>Needs</span>
                <span>Actions</span>
              </div>
              {visibleAdmissions.map((admission) => {
                const view = admission.dashboard_view || {};
                const detail = view.detail || {};
                const readyForDischarge = admission.clinical_status === 'ready_for_discharge';
                const expanded = expandedId === admission.admission_id;
                const tone = view.status?.tone || 'muted';
                const consentMissing = !admission.dashboard_data?.consent_complete;
                const highlightConsent = consentMissing && tone !== 'danger' && tone !== 'warning' && !readyForDischarge;
                const isIcu = admission.dashboard_data?.is_icu || String(admission.ward || '').toUpperCase().includes('ICU');
                return (
                  <article
                    key={admission.admission_id}
                    className={[
                      'admission-item',
                      `tone-${tone}`,
                      readyForDischarge ? 'ready-discharge-row' : '',
                      highlightConsent ? 'consent-missing-row' : '',
                      expanded ? 'expanded' : '',
                    ].filter(Boolean).join(' ')}
                  >
                    <div className="admission-item-main">
                      <div className="patient-col">
                        <div className="patient-name-row">
                          <span className="patient-name">{patientName(admission)}</span>
                          {isIcu && <span className="icu-badge">ICU</span>}
                        </div>
                        <div className="patient-location">{locationLine(admission)}</div>
                        <div className="patient-doctor">
                          Doctor: {detail.attending_physician || 'Not set'}
                        </div>
                        <div className="patient-meta">
                          <Link
                            to="/patient-card"
                            state={{ patient: admission.Patient }}
                            className="patient-link"
                          >
                            {admission.Patient?.patient_number || 'No ID'}
                          </Link>
                          <span> · Admission </span>
                          <Link
                            to="/admission-card"
                            state={{ admission }}
                            className="admission-link"
                          >
                            {admission.admission_id}
                          </Link>
                        </div>
                      </div>
                      <div className={`status-col tone-${tone}`}>
                        {statusLabel(view)}
                      </div>
                      <div className="needs-col">
                        {view.needs?.length ? (
                          view.needs.map((need) => (
                            <span key={need.key} className="need-chip">{need.label}</span>
                          ))
                        ) : (
                          <span className="needs-none">Nothing missing</span>
                        )}
                      </div>
                      <div className="row-actions">
                        <button
                          type="button"
                          className="show-more-btn"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => toggleExpanded(admission.admission_id)}
                          aria-expanded={expanded}
                        >
                          {expanded ? 'Hide' : 'Show more'}
                        </button>
                        <Link
                          to="/admission-card"
                          state={{ admission }}
                          className="open-chart-btn"
                        >
                          Open chart
                        </Link>
                      </div>
                    </div>
                    {expanded && (
                      <div className="admission-detail">
                        <div className="detail-clinical">
                          <div className="diagnosis-block">
                            <strong>Reason for stay</strong>
                            <p>{detail.diagnosis || 'No diagnosis yet'}</p>
                          </div>
                          <div className={`note-block${detail.progress_note?.missing ? ' missing' : ''}`}>
                            <strong>Last progress note</strong>
                            {detail.progress_note?.missing ? (
                              <p>Progress note missing</p>
                            ) : (
                              <>
                                <p className="note-meta">
                                  {[detail.progress_note.doctor_name, formatRelativeTime(detail.progress_note.at) || formatDateTime(detail.progress_note.at)].filter(Boolean).join(' · ')}
                                </p>
                                <p className="note-text">{detail.progress_note.text || 'No text in the last note.'}</p>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="detail-grid">
                          <div className={isCriticalVitals(detail.vitals) ? 'detail-critical' : undefined}>
                            <strong>Vitals</strong>
                            <p>{vitalsDetailText(detail.vitals)}</p>
                          </div>
                          {detail.glucose && (
                            <div className={isCriticalGlucose(detail.glucose) ? 'detail-critical' : undefined}>
                              <strong>Glucose</strong>
                              <p>
                                {detail.glucose.value == null
                                  ? 'No reading yet'
                                  : `${detail.glucose.band || 'Unknown'} · ${detail.glucose.value} · ${formatRelativeTime(detail.glucose.taken_at) || formatDateTime(detail.glucose.taken_at)}${detail.glucose.stale ? ' (overdue)' : ''}`}
                              </p>
                            </div>
                          )}
                          <div>
                            <strong>Treatment plan</strong>
                            <p>
                              {detail.plan?.has_active
                                ? `Active${detail.plan.created_at ? ` since ${formatRelativeTime(detail.plan.created_at)}` : ''}`
                                : 'No plan'}
                            </p>
                          </div>
                          <div>
                            <strong>Medicines</strong>
                            <p>{detail.meds?.label || 'No plan'}</p>
                          </div>
                          <div>
                            <strong>Patient history</strong>
                            <p>{detail.history?.label || 'Not started'}</p>
                          </div>
                          <div>
                            <strong>Discharge</strong>
                            <p>
                              {detail.discharge?.ready
                                ? `Ready for discharge · ${detail.discharge.take_home_label}`
                                : 'Still admitted'}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </>
      )}

      <div className="dashboard-footer">
        <p>Total open admissions: {visibleAdmissions.length}{selectedBranch ? ` (${admissions.length} all branches)` : ''}</p>
        <p className="auto-refresh-note">
          This dashboard auto-refreshes every 5 minutes
        </p>
      </div>
    </div>
  );
};

export default AdmissionsDashboard;
